import { QRCodeSVG } from 'qrcode.react';
import { useTranslation } from 'react-i18next';
import { type BookingDetails, type FlightOption } from '@/api/types';
import { formatDurationCompact, minutesUntil } from '@/utils/date';
import { useLocale } from '@/hooks/useLocale';
import { IcPlane } from '@/components/ui/icons';
import styles from './BoardingPass.module.css';

interface BoardingPassProps {
  booking: BookingDetails;
}

export function BoardingPass({ booking }: BoardingPassProps): JSX.Element {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const flight: FlightOption | undefined = booking.options?.find(
    (o) => o.id === booking.chosen_option_id,
  ) ?? booking.options?.[0];

  const checkInMinutes = flight ? minutesUntil(flight.depart_at) - 60 : 0;

  return (
    <div className={styles.wrap}>
      <div className={styles.pass}>
        <div className={styles.headerRow}>
          <IcPlane />
          <span className={styles.headerLabel}>
            {t('booking.check_in_in', { duration: formatDurationCompact(Math.max(0, checkInMinutes), locale) })}
          </span>
        </div>

        <div className={styles.routeRow}>
          <div className={styles.routeSide}>
            <div className={styles.iata}>{flight?.from_iata ?? '—'}</div>
            <div className={styles.iataMeta}>
              {flight ? new Date(flight.depart_at).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' }) : '—'}
            </div>
          </div>
          <div className={styles.routeMiddle}>
            <span className={styles.timeBetween}>
              {flight ? formatDurationCompact(flight.duration_minutes, locale) : '—'}
            </span>
          </div>
          <div className={`${styles.routeSide} ${styles.routeSideEnd}`}>
            <div className={styles.iata}>{flight?.to_iata ?? '—'}</div>
            <div className={styles.iataMeta}>
              {flight ? new Date(flight.arrive_at).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' }) : '—'}
            </div>
          </div>
        </div>

        <div className={styles.metaGrid}>
          <Cell label={t('booking.class.' + (flight?.class ?? 'economy'))} value={t('booking.actions.order_taxi') /* placeholder */ } hideValue />
          <Cell label={t('booking.terminal')} value={flight?.terminal ?? '—'} />
          <Cell label={t('booking.gate')} value={flight?.gate ?? '—'} />
          <Cell label={t('booking.seat')} value={flight?.seat_suggestion ?? '—'} />
        </div>
      </div>

      {booking.ticket_code && (
        <div className={styles.codeBlock}>
          <div className={styles.codeLabel}>
            {t('booking.ticket_code')}: <b>{booking.ticket_code}</b>
          </div>
          <div className={styles.qrWrap}>
            <QRCodeSVG value={booking.barcode_data ?? booking.ticket_code} size={160} />
          </div>
          <p className={styles.codeHint}>{t('booking.scan_to_retrieve')}</p>
        </div>
      )}
    </div>
  );
}

function Cell({ label, value, hideValue }: { label: string; value: string; hideValue?: boolean }): JSX.Element {
  return (
    <div className={styles.cell}>
      <div className={styles.cellLabel}>{label}</div>
      {!hideValue && <div className={styles.cellValue}>{value}</div>}
    </div>
  );
}
